import i18next from 'i18next';
import { AsyncState, Unit, unloadedAsyncState, Updater } from "widgets-for-react";
import { ChangePasswordState, initialChangePasswordState } from "forms/changePassword/changePasswordState";
import { CompanyState, initialCompanyState } from "forms/company/companyState";
import { initialInvoiceSelectorState, InvoiceSelectorState } from "forms/invoiceSelector/invoiceSelectorState";
import { initialPersonalInfoState, PersonalInfoState } from "forms/personalInfo/personalInfoState";
import {
  initialSubscriptionSelectorState,
  SubscriptionSelectorState
} from "forms/subscriptionSelector/subscriptionSelectorState";
import {
  ChangePaymentMethodState,
  initialChangePaymentMethodState
} from "./changePaymentMethod/changePaymentMethodState";
import { initialNewSubscriptionState, NewSubscriptionState } from "./newSubscription/newSubscriptionState";
import { StripeUpgradeSubscriptionState } from "./stripeUpgradeSubscription/stripeUpgradeSubscriptionState"
import { initialPayOpenInvoiceState, PayOpenInvoiceState } from "./payOpenInvoice/payOpenInvoiceState";
import { initialResumeSubscriptionState, ResumeSubscriptionState } from "./resumeSubscription/resumeSubscriptionState";
import * as Api from "./subscriptionApi";
import { initialStripeUpgradeSubscriptionState } from './stripeUpgradeSubscription/stripeUpgradeSubscriptionState';

export type UserSubscriptions = {
  OpenPayments: OpenPayment[];
  User: UserData;
  ActiveSubscription: ActiveSubscription[];
  TerminationDate: string;
  PaywallStatus: 'ValidTrial'
    | 'ValidSubscription'
    | 'ValidStudentSubscription'
    | 'ValidTeacherSubscription'
    | 'ValidStudentSubscriptionWithPausedSubscription'
    | 'ValidTeacherSubscriptionWithPausedSubscription'
    | 'StillValidTerminatedSubscription'
    | 'ValidSubscriptionWithPaymentIssues'
    | 'NoSubscription'
    | 'Blocked'
  HasOriginalTransactionKey: boolean;
  TrialDaysLeft: number;
  PausedSubscriptions: PausedSubscription[];
  IsStripeCustomer: boolean;
  StripeSubscription: StripeSubscription | undefined;
}

export type StripeSubscription = {
  NextPaymentDate: string;
  PaymentFrequency: string;
  Amount: number;
  TrialDaysLeft: number;
  CancelAt: string;
  ExternalId: string;
}

export type UserData = {
  ExpectedGraduationYear: number;
  Company: {
    Type: string;
    Address: string;
    VAT: string;
    Title: string;
    Country: { Id: number };
    Id: number;
    City: string;
    Postcode: string
  };
  Email: string;
  Address: string;
  Birthday: string;
  Gender: string;
  Postcode: string;
  Name: string;
  Language: { Id: number; Code: string };
  Username: string;
  PhoneNumber: string;
  Country: { Id: number };
  Id: number;
  Job: { Id: number };
  Surname: string
}

export type ActiveSubscription = {
  RecurrentPayment: {
    Status: string;
    NextPaymentDate: string;
    Title: string;
    Frequency: null;
    Id: number;
    OriginalPaymentType: null
  };
  DateStarted: string;
  Title: string;
  Id: number;
  Subscription: {
    Description: string;
    Price: number;
    Currency: string;
    CurrencyPaymentFrequency: string;
    Title: string;
    Id: number
  }
}

export type PausedSubscription = {
  DateStarted: string;
  DatePaused: string;
  Title: string;
  NextPaymentDateWhenPaused: string;
  Id: number;
  Subscription: {
    Description: string;
    Price: number;
    Currency: string;
    PaymentFrequency: string;
    Title: string;
    Id: number
  };
  SubscriptionLookup: { TypeId: string; LookupId: number; LookupValue: string }
}

export type OpenPayment = {
  Status: string;
  User: { Title: string; Id: number };
  PaymentReference: string;
  PaymentDate: string;
  Amount: number;
  Title: string;
  Id: number
}

export type Subscription = Partial<{
  Price: number; //
  Currency: string; //
  PaymentFrequency: string; //
  Title: string; //
  Id: number; //
}>

export type LookupData = {
  CompanyTypes: { Title: string }[];
  Countries: { Modified: string; Title: string; Id: number; Created: string }[];
  Jobs: { Modified: string; Title: string; Id: number; Created: string }[];
  Subscriptions: Subscription[];
  IDealIssuers: { Modified: string; Title: string; Id: number; BIC: string; Created: string }[];
  Languages: ({ Modified: string; Title: string; Id: number; Code: string; Created: string })[];
  Schools: { Modified: string; Title: string; Id: number; Created: string }[]
}

export type ChangePasswordResult = {
  Kind:"Success" | "Error"
}

export type SubscriptionResult = {
  Kind:"Redirect"
  RedirectUrl:string
} | { 
  Kind:"Success" | "Error"
}


export type SubscriptionRedirect = 
  | "subscription-cancel" | "subscription-failure" | "subscription-success"
  | "change-payment-method-cancel" | "change-payment-method-failure" | "change-payment-method-success"
  | "pay-open-invoice-cancel" | "pay-open-invoice-failure" | "pay-open-invoice-success"


export type SubscriptionStateCommon = {
  invoiceSelectorState:InvoiceSelectorState
  lookupData:AsyncState<LookupData>
  userSubscriptions:AsyncState<UserSubscriptions>
  createPendingShoppingCart:AsyncState<Unit>,
  subscribe:AsyncState<SubscriptionResult>,
  changeSubscription:AsyncState<SubscriptionResult>,
  pauseSubscription:AsyncState<Unit>
  payOpenPayment:AsyncState<SubscriptionResult>
  changePassword:AsyncState<ChangePasswordResult>,
  changePersonalInfo:AsyncState<Unit>,
  resumeSubscription:AsyncState<SubscriptionResult>
  startSubscriptionPortalSession: AsyncState<SubscriptionResult>
  startSubscriptionUpdatePaymentMethod: AsyncState<SubscriptionResult>
  changePaymentMethodInput:ChangePaymentMethodState
  editing?:
    { kind:"password", changePasswordState:ChangePasswordState } 
    | { kind:"personal info", personalInfoState:PersonalInfoState, invoiceToCompany:boolean, companyState:CompanyState } 
    | { kind:"subscription", inPlaceOf:ActiveSubscription, subscriptionSelectorState:SubscriptionSelectorState }
  asyncsWorkingDisableInput:() => boolean
  asyncsFailedReloadPage:() => boolean
  checkVATState: AsyncState<boolean>
}

export type SubscriptionState = SubscriptionStateCommon 
& ({
  kind:"change payment method flow" | "overview" | SubscriptionRedirect
} | {
  kind:"new subscription flow",
  newSubscriptionInput:NewSubscriptionState
} | {
  kind:"stripe subscription flow",
  stripeUpgradeSubscriptionInput:StripeUpgradeSubscriptionState
} | {
  kind:"resume subscription flow",
  resumeSubscriptionInput:ResumeSubscriptionState
} | {
  kind: "pay open payment flow"
  payOpenInvoiceInput:PayOpenInvoiceState
  openPayment:OpenPayment
} | {
  kind: "edit subscription flow"
  newSubscriptionInput:NewSubscriptionState
  inPlaceOf:ActiveSubscription
})


export const initialSubscriptionState: SubscriptionState = ({
  invoiceSelectorState:initialInvoiceSelectorState(),
  userSubscriptions:unloadedAsyncState(),
  subscribe:unloadedAsyncState(),
  changeSubscription:unloadedAsyncState(),
  pauseSubscription:unloadedAsyncState(),
  resumeSubscription:unloadedAsyncState(),
  payOpenPayment:unloadedAsyncState(),
  lookupData:Api.getLookupData(),
  changePassword:unloadedAsyncState(),
  changePersonalInfo:unloadedAsyncState(),
  createPendingShoppingCart:unloadedAsyncState(),
  startSubscriptionPortalSession: unloadedAsyncState(),
  startSubscriptionUpdatePaymentMethod: unloadedAsyncState(),
  changePaymentMethodInput:initialChangePaymentMethodState,
  kind:"overview",
  // utility to disable stuff in bulk when other async calls are running
  asyncsFailedReloadPage:function(this:SubscriptionState) {
    const asyncFailed = <a>(a:AsyncState<a>) : boolean => a.kind == "failed" || a.kind == "unauthorized"
    return asyncFailed(this.userSubscriptions) || asyncFailed(this.pauseSubscription) 
            || asyncFailed(this.lookupData)
            || asyncFailed(this.subscribe) || asyncFailed(this.changeSubscription)
            || asyncFailed(this.pauseSubscription) || asyncFailed(this.resumeSubscription)
            || asyncFailed(this.changePaymentMethodInput.changePaymentMethod)
            || asyncFailed(this.payOpenPayment) || asyncFailed(this.changePassword)
  },
  asyncsWorkingDisableInput:function(this:SubscriptionState) {
    const asyncWorking = <a>(a:AsyncState<a>) : boolean => a.kind != "loaded" && a.kind != "unloaded" && a.kind != "failed"
    return asyncWorking(this.userSubscriptions) || asyncWorking(this.pauseSubscription) 
            || asyncWorking(this.lookupData)
            || asyncWorking(this.subscribe) || asyncWorking(this.changeSubscription)
            || asyncWorking(this.pauseSubscription) || asyncWorking(this.resumeSubscription)
            || asyncWorking(this.changePaymentMethodInput.changePaymentMethod)
            || asyncWorking(this.payOpenPayment) || asyncWorking(this.changePassword)
  },
  checkVATState: unloadedAsyncState(),
})

export const reloadedSubscriptionState: SubscriptionState = ({
  ...initialSubscriptionState, 
  userSubscriptions:Api.loadUserSubscriptions()
})

export const subscriptionUpdaters = {
  userSubscriptions:(u:Updater<SubscriptionState["userSubscriptions"]>) : Updater<SubscriptionState> =>
    s => {
      const newUserSubscriptions = u(s.userSubscriptions)
      const newSubscriptionState = newUserSubscriptions.kind === 'loaded' && s.kind === "new subscription flow"
        ? ({...s, userSubscriptions:newUserSubscriptions, newSubscriptionInput:initialNewSubscriptionState(newUserSubscriptions.value.User)})
        : ({...s, userSubscriptions:newUserSubscriptions})

      if (newUserSubscriptions.kind == "loaded" && newUserSubscriptions.value.HasOriginalTransactionKey)
        return subscriptionUpdaters.newSubscriptionInput(i => ({...i, paymentMethod:"OriginalToken"}))(newSubscriptionState)
      else
        return newSubscriptionState
    },
  updateSubscriptionState: (updater: Updater<SubscriptionState>) => {
    return (state: SubscriptionState) => {
      return updater(state)
    } 
  },
  lookupData:(u:Updater<SubscriptionState["lookupData"]>) : Updater<SubscriptionState> =>
    s => ({...s, lookupData:u(s.lookupData)}),
  invoiceSelectorState:(u:Updater<SubscriptionState["invoiceSelectorState"]>) : Updater<SubscriptionState> =>
    s => ({...s, invoiceSelectorState:u(s.invoiceSelectorState)}),
  subscribe:(u:Updater<SubscriptionState["subscribe"]>) : Updater<SubscriptionState> =>
    s => {
      var newSubscribe = u(s.subscribe)
      if (newSubscribe.kind == "loaded" && newSubscribe.value.Kind == "Redirect") {
        var redirectUrl = newSubscribe.value.RedirectUrl
        window.location.href = redirectUrl
      }
      return ({...s, subscribe:newSubscribe})
    },
  createPendingShoppingCart:(u:Updater<SubscriptionState["createPendingShoppingCart"]>) : Updater<SubscriptionState> =>
    s => ({...s, createPendingShoppingCart:u(s.createPendingShoppingCart)}),
  payOpenPayment:(u:Updater<SubscriptionState["payOpenPayment"]>) : Updater<SubscriptionState> =>
    s => ({...s, payOpenPayment:u(s.payOpenPayment)}),
  stripeUpgradeSubscriptionInput:(u:Updater<StripeUpgradeSubscriptionState>) : Updater<SubscriptionState> =>
    s => s.kind != "stripe subscription flow" ? s : ({...s, stripeUpgradeSubscriptionInput:u(s.stripeUpgradeSubscriptionInput)}),
  newSubscriptionInput:(u:Updater<NewSubscriptionState>) : Updater<SubscriptionState> =>
    s => s.kind != "new subscription flow" ? s : ({...s, newSubscriptionInput:u(s.newSubscriptionInput)}),
  startEditSubscriptionFlow:(inPlaceOf:ActiveSubscription) : Updater<SubscriptionState> =>
    s => s.userSubscriptions.kind != "loaded" ? s :
      ({...reloadedSubscriptionState, 
        kind:"edit subscription flow", 
        inPlaceOf:inPlaceOf,
        newSubscriptionInput:initialNewSubscriptionState(s.userSubscriptions.value.User),
        lookupData:s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
      }),
  startNewSubscriptionFlow:() : Updater<SubscriptionState> =>
    s =>
      {
        return ({
          ...reloadedSubscriptionState,
          kind: "stripe subscription flow",
          stripeUpgradeSubscriptionInput: initialStripeUpgradeSubscriptionState(s.userSubscriptions.kind === "loaded" ? s.userSubscriptions.value.User : undefined),
          createPendingShoppingCart: Api.createPendingShoppingCart(), // TODO: Not sure if we still need this...
          lookupData: s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
        })
      },
  changePaymentMethodInput:(u:Updater<SubscriptionState["changePaymentMethodInput"]>) : Updater<SubscriptionState> =>
    s => ({...s, changePaymentMethodInput:u(s.changePaymentMethodInput)}),
  startChangePaymentMethodFlow:() : Updater<SubscriptionState> =>
    s => 
      ({...reloadedSubscriptionState, 
        kind:"change payment method flow", 
        changePaymentMethodInput:{...s.changePaymentMethodInput, 
          changePaymentMethod:unloadedAsyncState()
        },
        lookupData:s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
      }),
  onAsyncStartSubscriptionPortalSession: (u:Updater<SubscriptionState["startSubscriptionUpdatePaymentMethod"]>): Updater<SubscriptionState> => 
    s => {
      let updatedState = u(s.startSubscriptionPortalSession)

      if (updatedState.kind === "loaded") {
        // Navigate the browser once we have received a valid portal session url
        if (updatedState.value.Kind === "Redirect" && updatedState.value.RedirectUrl) {
          window.location.href = updatedState.value.RedirectUrl
        }
        else {
          // If we received a failed response, reset the loading state
          return ({
            ...s, startSubscriptionPortalSession: unloadedAsyncState()
          });
        }
      }

      // Otherwise keep updating the loading state untill the request resolves to a response
      return ({
        ...s, startSubscriptionPortalSession: updatedState
      });
    },
  onAsyncStartSubscriptionUpdatePaymentMethodSession: (u:Updater<SubscriptionState["startSubscriptionPortalSession"]>): Updater<SubscriptionState> => 
      s => {
        let updatedState = u(s.startSubscriptionUpdatePaymentMethod)
  
        if (updatedState.kind === "loaded") {
          // Navigate the browser once we have received a valid portal session url
          if (updatedState.value.Kind === "Redirect" && updatedState.value.RedirectUrl) {
            window.location.href = updatedState.value.RedirectUrl
          }
          else {
            // If we received a failed response, reset the loading state
            return ({
              ...s, startSubscriptionUpdatePaymentMethod: unloadedAsyncState()
            });
          }
        }
  
        // Otherwise keep updating the loading state untill the request resolves to a response
        return ({
          ...s, startSubscriptionUpdatePaymentMethod: updatedState
        });
      },
  startSubscriptionPortalSession:(): Updater<SubscriptionState> =>
    s => ({
      ...s,
      kind: 'overview',
      startSubscriptionPortalSession: Api.startSubscriptionPortalSession()
    }),
  startSubscriptionUpdatePaymentMethodPortalSession:(): Updater<SubscriptionState> =>
    s => ({
      ...s,
      kind: 'overview',
      startSubscriptionUpdatePaymentMethod: Api.startSubscriptionUpdatePaymentMethod()
    }),
  payOpenInvoiceInput:(u:Updater<PayOpenInvoiceState>) : Updater<SubscriptionState> =>
    s => s.kind != "pay open payment flow" ? s : 
      ({...s, payOpenInvoiceInput:u(s.payOpenInvoiceInput)}),
  startPayOpenPaymentInputFlow:(openPayment:OpenPayment) : Updater<SubscriptionState> =>
    s => s.userSubscriptions.kind != "loaded" ? s :
      ({...reloadedSubscriptionState, 
        kind:"pay open payment flow", 
        payOpenInvoiceInput:{
          ...initialPayOpenInvoiceState(s.userSubscriptions.value.User),
          payOpenPayment:unloadedAsyncState()
        },
        openPayment:openPayment,
        lookupData:s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
      }),
  resumeSubscriptionInput:(u:Updater<ResumeSubscriptionState>) : Updater<SubscriptionState> =>
    s => 
      (s.kind == "resume subscription flow") ?
        ({...s, resumeSubscriptionInput:u(s.resumeSubscriptionInput)})
      : s,
  startResumeSubscriptionFlow:(subscriptionToResume:PausedSubscription) : Updater<SubscriptionState> =>
    s => 
      {
        let s1:SubscriptionState = ({...reloadedSubscriptionState, 
          kind:"resume subscription flow", 
          resumeSubscriptionInput:initialResumeSubscriptionState(subscriptionToResume),
          lookupData:s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
        })
        if (s1.userSubscriptions.kind == "loaded" && s1.userSubscriptions.value.HasOriginalTransactionKey)
          return subscriptionUpdaters.invokeResumeApi(s1)
        return s1
      },
  resumeSubscription:(u:Updater<SubscriptionState["resumeSubscription"]>) : Updater<SubscriptionState> =>
    s => {
      let s1 = ({...s, resumeSubscription:u(s.resumeSubscription)})
      if (s.pauseSubscription.kind != s1.pauseSubscription.kind && s1.pauseSubscription.kind == "loaded")
        s1 = reloadedSubscriptionState
      return s1
    },
  pauseSubscription:(u:Updater<SubscriptionState["pauseSubscription"]>) : Updater<SubscriptionState> =>
    s => {
      let s1 = ({...s, pauseSubscription:u(s.pauseSubscription)})
      if (s.pauseSubscription.kind != s1.pauseSubscription.kind && s1.pauseSubscription.kind == "loaded")
        s1 = reloadedSubscriptionState
      return s1
    },
  requestSubscriptionPause:(subscription:ActiveSubscription) : Updater<SubscriptionState> => s => 
    s.pauseSubscription.kind == "unloaded" ||
    s.pauseSubscription.kind == "loaded" ?
    ({...s, pauseSubscription:Api.pauseActiveSubscription(subscription) })
  : s,
  overview:() : SubscriptionState => reloadedSubscriptionState,
  startStripeCheckoutSessionApi:(s:SubscriptionState) : SubscriptionState =>
    (s.kind == "stripe subscription flow") && 
    (s.subscribe.kind == "unloaded" || s.subscribe.kind == "loaded") ?
      ({...s, subscribe:Api.startStripeCheckoutSession(s.stripeUpgradeSubscriptionInput) })
    : ({...s, kind:"overview"}),
  invokeSubscribeNowApi:(s:SubscriptionState) : SubscriptionState => 
      (s.kind == "new subscription flow" || s.kind == "edit subscription flow") && 
      (s.subscribe.kind == "unloaded" || s.subscribe.kind == "loaded") ?
        ({...s, subscribe:Api.subscribeNow(s.newSubscriptionInput) })
      : ({...s, kind:"overview"}),
  invokeResumeApi:(s:SubscriptionState) : SubscriptionState => 
      s.kind == "resume subscription flow" &&
      (s.resumeSubscription.kind == "unloaded" ||
      s.resumeSubscription.kind == "loaded") ?
        ({...s, resumeSubscription:Api.resumePausedSubscription(s.resumeSubscriptionInput) })
      : ({...s, kind:"overview"}),
  invokeChangePaymentMethodApi:(s:SubscriptionState) : SubscriptionState => 
    (s.changePaymentMethodInput.changePaymentMethod.kind == "unloaded" ||
      s.changePaymentMethodInput.changePaymentMethod.kind == "loaded") &&
      s.changePaymentMethodInput.paymentMethodState.input.paymentType != undefined ?
        ({...s, changePaymentMethodInput:{...s.changePaymentMethodInput, changePaymentMethod:Api.changePaymentMethod(s.changePaymentMethodInput) }})
      : ({...s, kind:"overview"}),
  invokePayOpenPaymentApi:(s:SubscriptionState) : SubscriptionState => 
    s.kind != "pay open payment flow" ? s :
    ( s.payOpenPayment.kind == "unloaded" ||
      s.payOpenPayment.kind == "loaded") &&
      s.payOpenInvoiceInput.paymentMethodState.input.paymentType != undefined && s.kind == "pay open payment flow" ?
        ({...s, payOpenPayment:Api.payOpenInvoice(s.openPayment, s.payOpenInvoiceInput.paymentMethodState.input.paymentType, s.payOpenInvoiceInput.paymentMethodState.input.iDealIssuer) })
      : ({...s, kind:"overview"}),
  subscriptionRedirect: (variant:SubscriptionRedirect): Updater<SubscriptionState> =>
    s => ({ ...s, kind:variant }),  
  cancelSubFlow: (): Updater<SubscriptionState> =>
    s => ({...s, editing:undefined}),
  changePassword: (u:Updater<SubscriptionState["changePassword"]>): Updater<SubscriptionState> =>
    s => {
      const newChangePassword = u(s.changePassword)
      const s1 = ({ ...s, changePassword:newChangePassword })
      if (newChangePassword.kind == "loaded" && newChangePassword.value.Kind == "Success") {
        alert(i18next.t("subscription:PasswordChangedSuccessfully"))
        return ({...s1, editing:undefined })
      } else if (newChangePassword.kind == "loaded" && newChangePassword.value.Kind == "Error") {
        alert(i18next.t("subscription:ErrorChangingPassword"))
        return ({...s1 })
      }

      return s1
    },
  startChangePasswordSubFlow: (): Updater<SubscriptionState> =>
    s => ({...s, editing:{ kind:"password", changePasswordState:initialChangePasswordState }}),
  changePasswordState: (u:Updater<ChangePasswordState>): Updater<SubscriptionState> =>
    s => s.editing?.kind != "password" ? s : 
      ({ ...s, editing:{...s.editing, changePasswordState:u(s.editing.changePasswordState)} }),  
  invokeChangePasswordApi:(s:SubscriptionState) : SubscriptionState => 
    (s.changePassword.kind == "unloaded" ||
      s.changePassword.kind == "loaded") &&
      s.editing != undefined &&
      s.editing.kind == "password" && s.editing.changePasswordState.isValid() ?
        ({...s, changePassword:Api.changePassword(s.editing.changePasswordState) })
      : ({...s}),

  changePersonalInfo: (u:Updater<SubscriptionState["changePersonalInfo"]>): Updater<SubscriptionState> =>
    s => {
      const newChangePersonalInfo = u(s.changePersonalInfo)
      return ({ ...s, 
        changePersonalInfo:newChangePersonalInfo, 
        userSubscriptions:newChangePersonalInfo.kind == "loaded" && s.changePersonalInfo.kind != "loaded" ? Api.loadUserSubscriptions() : s.userSubscriptions })
    },  
  startChangePersonalInfoSubFlow: (): Updater<SubscriptionState> =>
    s => s.userSubscriptions.kind != "loaded" ? s : ({...s, 
      lookupData:s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
      editing:{ kind:"personal info", personalInfoState:initialPersonalInfoState(s.userSubscriptions.value.User), invoiceToCompany:!!s.userSubscriptions.value.User.Company, companyState:initialCompanyState(s.userSubscriptions.value.User) }}),
  changePersonalInfoPersonalInfoState: (u:Updater<PersonalInfoState>): Updater<SubscriptionState> =>
    s => s.editing?.kind != "personal info" ? s : 
      ({ ...s, editing:{...s.editing, personalInfoState:u(s.editing.personalInfoState)} }),  
  changePersonalInfoInvoiceToCompany: (u:Updater<boolean>): Updater<SubscriptionState> =>
    s => s.editing?.kind != "personal info" ? s : 
      ({ ...s, editing:{...s.editing, invoiceToCompany:u(s.editing.invoiceToCompany)} }),  
  changePersonalInfoCompanyState: (u:Updater<CompanyState>): Updater<SubscriptionState> =>
    s => s.editing?.kind != "personal info" ? s : 
      ({ ...s, editing:{...s.editing, companyState:u(s.editing.companyState)} }),  
  invokeChangePersonalInfoApi:(s:SubscriptionState) : SubscriptionState => 
    (s.changePersonalInfo.kind == "unloaded" ||
      s.changePersonalInfo.kind == "loaded") &&
      s.editing != undefined &&
      s.editing.kind == "personal info" && s.editing.personalInfoState.isValid(false) ?
        ({...s, changePersonalInfo:Api.changePersonalInfo(s.editing.personalInfoState, s.editing.invoiceToCompany, s.editing.companyState), editing:undefined })
      : ({...s}),

  startChangeSubscriptionSubFlow: (inPlaceOf:ActiveSubscription): Updater<SubscriptionState> =>
    s => ({...s, 
      lookupData:s.lookupData.kind == "unloaded" ? Api.getLookupData() : s.lookupData,
      editing:{ kind:"subscription", inPlaceOf:inPlaceOf, subscriptionSelectorState:
        s.userSubscriptions.kind == "loaded" && s.lookupData.kind == "loaded" && s.userSubscriptions.value.ActiveSubscription.length > 0 ?
          initialSubscriptionSelectorState(s.lookupData.value.Subscriptions.find(
            ((currentSubscriptionId:number) => (s:Subscription) => s.Id == currentSubscriptionId)(s.userSubscriptions.value.ActiveSubscription[0].Subscription.Id))
          )
        : initialSubscriptionSelectorState() 
      }}),
  changeSubscriptionState: (u:Updater<SubscriptionSelectorState>): Updater<SubscriptionState> =>
    s => s.editing?.kind != "subscription" ? s : 
      ({ ...s, editing:{...s.editing, subscriptionSelectorState:u(s.editing.subscriptionSelectorState)} }),  
  invokeChangeSubscriptionApi:(s:SubscriptionState) : SubscriptionState => 
    (s.subscribe.kind == "unloaded" ||
      s.subscribe.kind == "loaded") &&
      s.editing != undefined &&
      s.editing.kind == "subscription" && s.editing.subscriptionSelectorState.isValid() ?
        ({...s, changeSubscription:Api.changeSubscription(s.editing.subscriptionSelectorState.input) })
      : ({...s}),
  changeSubscription:(u:Updater<SubscriptionState["changeSubscription"]>) : Updater<SubscriptionState> =>
    s => {
      let newChangeSubscription = u(s.changeSubscription)
      let s1 = ({...s, changeSubscription:newChangeSubscription})
      if (newChangeSubscription.kind == "loaded" && newChangeSubscription.value.Kind == "Success") {
        return ({...s1, editing:undefined, userSubscriptions:Api.loadUserSubscriptions() })
      } else if (newChangeSubscription.kind == "loaded" && newChangeSubscription.value.Kind == "Error") {
        alert(i18next.t("subscription:ErrorChangingSubscription"))
        return ({...s1 })
      }
      return s1
    },
    checkVATState:
    (
      u: Updater<SubscriptionState['checkVATState']>
    ): Updater<SubscriptionState> =>
    (s) => {
      const n = u(s.checkVATState)
      if (n.kind == "loaded" && n.value === false) {
        const authMain = document.getElementById("auth__main")
        if (authMain != null) {
          const errorBox = document.getElementById('auth__error-box')
          if (errorBox != null) authMain.scrollTo({top: errorBox.offsetTop, behavior: 'smooth'})
          else authMain.scrollTo({top: 0, behavior: 'smooth'})
        }
      }
      const loaded =
        n.kind == 'loaded' &&
        n.value &&
        (s.changePersonalInfo.kind == 'unloaded' ||
          s.changePersonalInfo.kind == 'loaded')
      return {
        ...s,
        checkVATState: n,
        changePersonalInfo:
          loaded &&
          s.editing != undefined &&
          s.editing.kind == 'personal info' &&
          s.editing.personalInfoState.isValid(false)
            ? Api.changePersonalInfo(
                s.editing.personalInfoState,
                s.editing.invoiceToCompany,
                s.editing.companyState
              )
            : s.changePersonalInfo,
        editing:
          loaded &&
          s.editing != undefined &&
          s.editing.kind == 'personal info' &&
          s.editing.personalInfoState.isValid(false)
            ? undefined
            : s.editing,
      }
    },
}
